import type { AIProvider, Flashcard, GenerationOptions } from '../types';

const POLLINATION_ENDPOINT = 'https://text.pollinations.ai/openai';
const GROQ_ENDPOINT = 'https://api.groq.com/openai/v1/chat/completions';
const GEMINI_MODELS_ENDPOINT = 'https://generativelanguage.googleapis.com/v1beta/models';
const GEMINI_GENERATE_ENDPOINT = 'https://generativelanguage.googleapis.com/v1beta/models';
const OPENROUTER_ENDPOINT = 'https://openrouter.ai/api/v1/chat/completions';

const WORKER_ENDPOINT = 'https://flashcard-generator-plus-api-proxy.driveclone.workers.dev/api/generate';

const ANTI_OVERTHINK = 'CRITICAL INSTRUCTION: Do not output internal reasoning, <think> tags, or step-by-step thinking. Output ONLY the final direct response.';

export interface ModelInfo {
  id: string;
  name: string;
  context: string;
}

export interface GenerationMetadata {
  provider: string;
  model: string;
  cardsGenerated: number;
  chunksUsed: number;
}

export interface GenerationResult {
  cards: Flashcard[];
  metadata: GenerationMetadata;
}

function buildSystemPrompt(options: GenerationOptions): string {
  const { difficulty, includeHints, outputFormat, language } = options;

  let formatInstructions = '';
  switch (outputFormat) {
    case 'cloze':
      formatInstructions = 'Generate cloze deletion cards where the user fills in blanks.';
      break;
    case 'reversible':
      formatInstructions = 'Generate reversible cards that can be studied from both sides.';
      break;
    default:
      formatInstructions = 'Generate basic question/answer cards.';
  }

  return `You are an expert flashcard creator. Your task is to generate high-quality flashcards from the provided content.

Instructions:
- Generate exactly ${options.cardCount} flashcards
- Difficulty level: ${difficulty}
- Language: ${language}
- ${formatInstructions}
- Each card must have a clear front (question/prompt) and back (answer)
${includeHints ? '- Include a helpful hint for each card' : ''}
- Return the cards in strict JSON format with this structure: {"cards": [{"front": "...", "back": "..."${includeHints ? ', "hint": "..."' : ''}}]}
- Generate exactly the requested number of cards
- Make cards concise but complete (front under 120 chars, back under 200 chars)
- Avoid ambiguous questions or answers
- Use the provided content as the sole source for generating cards

${ANTI_OVERTHINK}`;
}

function buildUserMessage(text: string, cardCount: number): string {
  return `Please generate ${cardCount} flashcards from the following content.\n\nContent:\n${text.slice(0, 8000)}`;
}

function extractJsonFromText(text: string): string {
  const cleanText = text.trim();

  const jsonBlockMatch = cleanText.match(/```json\s*([\s\S]*?)\s*```/);
  if (jsonBlockMatch) {
    return jsonBlockMatch[1].trim();
  }

  const codeBlockMatch = cleanText.match(/```\s*([\s\S]*?)\s*```/);
  if (codeBlockMatch) {
    return codeBlockMatch[1].trim();
  }

  const jsonMatch = cleanText.match(/(\{[\s\S]*\}|\[[\s\S]*\])/);
  if (jsonMatch) {
    return jsonMatch[1].trim();
  }

  return cleanText;
}

function stripThinkTags(content: string): string {
  return content.replace(/<think>[\s\S]*?<\/think>/gi, '').trim();
}

function parseFlashcards(cards: unknown[], options: GenerationOptions): Flashcard[] {
  if (!Array.isArray(cards)) {
    throw new Error('AI response did not contain a valid cards array');
  }

  return cards.map((card, index) => {
    const cardData = card as Record<string, string>;
    if (!cardData.front || !cardData.back) {
      return null;
    }

    return {
      id: `card-${Date.now()}-${index}`,
      front: cardData.front.trim(),
      back: cardData.back.trim(),
      hint: cardData.hint?.trim() || '',
      tags: options.tags || [],
      difficulty: (cardData.difficulty as 'easy' | 'medium' | 'hard') || options.difficulty,
      createdAt: new Date().toISOString(),
      interval: 0,
      easeFactor: 2.5,
      repetitions: 0,
    } as Flashcard;
  }).filter(Boolean) as Flashcard[];
}

export async function generateWithPollination(text: string, options: GenerationOptions, model: string = 'openai-fast'): Promise<Flashcard[]> {
  const prompt = buildSystemPrompt(options);
  const userMessage = buildUserMessage(text, options.cardCount);

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 60000);

  const response = await fetch(POLLINATION_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model,
      messages: [
        { role: 'system', content: prompt },
        { role: 'user', content: userMessage },
      ],
      stream: false,
    }),
    signal: controller.signal,
  });

  clearTimeout(timeoutId);

  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unknown error');
    if (response.status === 429) {
      throw new Error('Rate limit exceeded. Please wait 15 seconds between requests.');
    }
    if (response.status === 402 || response.status === 403) {
      throw new Error(`Model "${model}" requires payment. Try "openai-fast" instead.`);
    }
    if (response.status === 404) {
      throw new Error(`Model "${model}" not found. Use "openai-fast", "openai", or "gpt-oss".`);
    }
    throw new Error(`Pollination error ${response.status}: ${errorText.slice(0, 200)}`);
  }

  const rawText = await response.text();
  let content = '';

  try {
    const data = JSON.parse(rawText);
    if (data?.choices?.[0]?.message?.content) {
      content = data.choices[0].message.content;
    } else if (data?.choices?.[0]?.text) {
      content = data.choices[0].text;
    } else if (typeof data === 'string') {
      content = data;
    } else {
      content = rawText;
    }
  } catch {
    content = rawText;
  }

  content = stripThinkTags(content);

  if (!content) {
    console.error('Pollination response:', rawText);
    throw new Error('Pollination returned empty response');
  }

  const jsonStr = extractJsonFromText(content);
  const parsed = JSON.parse(jsonStr);
  return parseFlashcards(parsed.cards || parsed, options);
}

export async function generateWithGroq(text: string, options: GenerationOptions, apiKey: string, model: string): Promise<Flashcard[]> {
  const prompt = buildSystemPrompt(options);
  const userMessage = buildUserMessage(text, options.cardCount);

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Provider': 'groq',
  };
  if (apiKey) headers['X-User-API-Key'] = apiKey;

  const response = await fetch(WORKER_ENDPOINT, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      messages: [
        { role: 'system', content: prompt },
        { role: 'user', content: userMessage },
      ],
      model,
      temperature: 0.7,
      max_tokens: 4000,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(`Groq error: ${response.status} - ${error.error?.message || response.statusText}`);
  }

  const data = await response.json();
  const content = data.choices?.[0]?.message?.content;

  if (!content) {
    throw new Error('Groq returned empty response');
  }

  const jsonStr = extractJsonFromText(stripThinkTags(content));
  const parsed = JSON.parse(jsonStr);
  return parseFlashcards(parsed.cards || parsed, options);
}

export async function generateWithGemini(text: string, options: GenerationOptions, apiKey: string, model: string): Promise<Flashcard[]> {
  const modelName = model.includes('/') ? model.split('/')[1] : model;
  const prompt = `${buildSystemPrompt(options)}

${buildUserMessage(text, options.cardCount)}

Return ONLY valid JSON.`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Provider': 'gemini',
  };
  if (apiKey) headers['X-User-API-Key'] = apiKey;

  const response = await fetch(WORKER_ENDPOINT, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      model: modelName,
      contents: [{ parts: [{ text: prompt }] }],
      generationConfig: {
        temperature: 0.7,
        maxOutputTokens: 4096,
      },
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(`Gemini error ${response.status}: ${error.error?.message || 'Unknown error'}`);
  }

  const data = await response.json();
  const content = data.candidates?.[0]?.content?.parts?.[0]?.text;

  if (!content) {
    console.error('Gemini response:', data);
    throw new Error('Gemini returned empty response');
  }

  const jsonStr = extractJsonFromText(stripThinkTags(content));
  const parsed = JSON.parse(jsonStr);
  return parseFlashcards(parsed.cards || parsed, options);
}

export async function generateWithOpenRouter(text: string, options: GenerationOptions, apiKey: string, model: string): Promise<Flashcard[]> {
  const prompt = buildSystemPrompt(options);
  const userMessage = buildUserMessage(text, options.cardCount);

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Provider': 'openrouter',
  };
  if (apiKey) headers['X-User-API-Key'] = apiKey;

  const response = await fetch(WORKER_ENDPOINT, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      messages: [
        { role: 'system', content: prompt },
        { role: 'user', content: userMessage },
      ],
      model,
      temperature: 0.7,
      max_tokens: 4000,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(`OpenRouter error: ${response.status} - ${error.error?.message || response.statusText}`);
  }

  const data = await response.json();
  const content = data.choices?.[0]?.message?.content;

  if (!content) {
    throw new Error('OpenRouter returned empty response');
  }

  const jsonStr = extractJsonFromText(stripThinkTags(content));
  const parsed = JSON.parse(jsonStr);
  return parseFlashcards(parsed.cards || parsed, options);
}

export async function generateFlashcards(
  text: string,
  options: GenerationOptions,
  provider?: AIProvider
): Promise<GenerationResult> {
  const sourceText = text.trim();
  if (!sourceText) throw new Error('No source text provided');

  let cards: Flashcard[];
  let providerName = provider?.name || 'Pollination AI';
  let modelName = provider?.selectedModel || 'openai-fast';

  try {
    if (provider?.apiKey || provider?.serverKeyAvailable) {
      const key = provider.apiKey || '';
      switch (provider.id) {
        case 'groq':
          cards = await generateWithGroq(sourceText, options, key, provider.selectedModel);
          providerName = provider.name;
          modelName = provider.selectedModel;
          break;
        case 'gemini':
          cards = await generateWithGemini(sourceText, options, key, provider.selectedModel);
          providerName = provider.name;
          modelName = provider.selectedModel;
          break;
        case 'openrouter':
          cards = await generateWithOpenRouter(sourceText, options, key, provider.selectedModel);
          providerName = provider.name;
          modelName = provider.selectedModel;
          break;
        default:
          cards = await generateWithPollination(sourceText, options, provider.selectedModel || 'openai-fast');
      }
    } else {
      cards = await generateWithPollination(sourceText, options, 'openai-fast');
    }
  } catch (error) {
    console.error('Generation error:', error);
    throw error;
  }

  if (cards.length === 0) {
    throw new Error('No flashcards were generated');
  }

  const finalCards = cards.slice(0, options.cardCount);

  return {
    cards: finalCards,
    metadata: {
      provider: providerName,
      model: modelName,
      cardsGenerated: finalCards.length,
      chunksUsed: 1,
    },
  };
}

export async function generateAdditionalCards(
  text: string,
  count: number,
  options: GenerationOptions,
  provider?: AIProvider
): Promise<GenerationResult> {
  return generateFlashcards(text, { ...options, cardCount: count }, provider);
}

export async function fetchGroqModels(apiKey: string): Promise<{ models: ModelInfo[]; valid: boolean; error?: string }> {
  if (!apiKey) return { models: [], valid: false, error: 'No API key provided' };

  try {
    const response = await fetch('https://api.groq.com/openai/v1/models', {
      headers: { 'Authorization': `Bearer ${apiKey}` },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      return { models: [], valid: false, error: error.error?.message || `HTTP ${response.status}` };
    }

    const data = await response.json();
    const models: ModelInfo[] = (data.data || [])
      .filter((m: any) => m.id && (m.id.includes('llama') || m.id.includes('mixtral') || m.id.includes('qwen') || m.id.includes('gemma') || m.id.includes('gpt-oss')))
      .map((m: any) => ({
        id: m.id,
        name: m.name || m.id.replace(/-/g, ' ').replace(/\//g, ' - '),
        context: m.context_length
          ? m.context_length > 100000
            ? '128K'
            : m.context_length > 32000
              ? '32K'
              : '8K'
          : m.id.includes('70b') || m.id.includes('120b') ? '128K' : '8K',
      }));

    if (models.length === 0) {
      return { models: [], valid: false, error: 'No compatible models found' };
    }

    return { models, valid: true };
  } catch (error) {
    return { models: [], valid: false, error: (error as Error).message };
  }
}

export async function fetchGroqModelsViaWorker(): Promise<{ models: ModelInfo[]; valid: boolean; error?: string }> {
  try {
    const response = await fetch(`${WORKER_ENDPOINT.replace('/api/generate', '')}/api/models?provider=groq`);
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      return { models: [], valid: false, error: error.error || `HTTP ${response.status}` };
    }
    const data = await response.json();
    const models: ModelInfo[] = (data.data || [])
      .filter((m: any) => m.id && (m.id.includes('llama') || m.id.includes('mixtral') || m.id.includes('qwen') || m.id.includes('gemma') || m.id.includes('gpt-oss')))
      .map((m: any) => ({
        id: m.id,
        name: m.name || m.id.replace(/-/g, ' ').replace(/\//g, ' - '),
        context: m.context_length
          ? m.context_length > 100000
            ? '128K'
            : m.context_length > 32000
              ? '32K'
              : '8K'
          : m.id.includes('70b') || m.id.includes('120b') ? '128K' : '8K',
      }));
    return models.length > 0 ? { models, valid: true } : { models: [], valid: false, error: 'No compatible models found' };
  } catch (error) {
    return { models: [], valid: false, error: (error as Error).message };
  }
}

export async function fetchGeminiModels(apiKey: string): Promise<{ models: ModelInfo[]; valid: boolean; error?: string }> {
  if (!apiKey) return { models: [], valid: false, error: 'No API key provided' };

  try {
    const url = `https://generativelanguage.googleapis.com/v1beta/models?key=${encodeURIComponent(apiKey)}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      let errorMsg = `HTTP ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData.error) {
          errorMsg = errorData.error.message || errorData.error.details || errorMsg;
        }
      } catch {}
      return { models: [], valid: false, error: errorMsg };
    }

    const data = await response.json();

    if (!data.models || !Array.isArray(data.models)) {
      console.error('Unexpected Gemini response structure:', data);
      return { models: [], valid: false, error: 'Unexpected response format' };
    }

    const generateModels = data.models.filter((m: any) => {
      if (!m.name) return false;
      return m.supportedGenerationMethods?.includes('generateContent');
    });

    if (generateModels.length === 0) {
      return { models: [], valid: false, error: 'No generateContent models available' };
    }

    const models: ModelInfo[] = generateModels.slice(0, 20).map((m: any) => {
      const nameParts = m.name.split('/');
      const modelId = nameParts[nameParts.length - 1];
      const displayName = m.displayName || modelId;

      let context = 'Unknown';
      if (m.inputTokenLimit) {
        if (m.inputTokenLimit >= 1000000) {
          context = `${Math.round(m.inputTokenLimit / 1000000)}M`;
        } else {
          context = `${Math.round(m.inputTokenLimit / 1000)}K`;
        }
      }

      return {
        id: m.name,
        name: displayName.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        context,
      };
    });

    return { models, valid: true };
  } catch (error) {
    console.error('Gemini fetch error:', error);
    return { models: [], valid: false, error: (error as Error).message };
  }
}

export async function fetchGeminiModelsViaWorker(): Promise<{ models: ModelInfo[]; valid: boolean; error?: string }> {
  try {
    const response = await fetch(`${WORKER_ENDPOINT.replace('/api/generate', '')}/api/models?provider=gemini`);
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      return { models: [], valid: false, error: error.error || `HTTP ${response.status}` };
    }
    const data = await response.json();
    if (!data.models || !Array.isArray(data.models)) {
      return { models: [], valid: false, error: 'Unexpected response format' };
    }
    const generateModels = data.models.filter((m: any) => m.name && m.supportedGenerationMethods?.includes('generateContent'));
    if (generateModels.length === 0) {
      return { models: [], valid: false, error: 'No generateContent models available' };
    }
    const models: ModelInfo[] = generateModels.slice(0, 20).map((m: any) => {
      const nameParts = m.name.split('/');
      const modelId = nameParts[nameParts.length - 1];
      const displayName = m.displayName || modelId;
      let context = 'Unknown';
      if (m.inputTokenLimit) {
        context = m.inputTokenLimit >= 1000000 ? `${Math.round(m.inputTokenLimit / 1000000)}M` : `${Math.round(m.inputTokenLimit / 1000)}K`;
      }
      return { id: m.name, name: displayName.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()), context };
    });
    return { models, valid: true };
  } catch (error) {
    return { models: [], valid: false, error: (error as Error).message };
  }
}

export async function fetchOpenRouterModels(apiKey: string): Promise<{ models: ModelInfo[]; valid: boolean; error?: string }> {
  try {
    const headers: Record<string, string> = {};
    if (apiKey) headers['Authorization'] = `Bearer ${apiKey}`;

    const response = await fetch('https://openrouter.ai/api/v1/models', { headers });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      return { models: [], valid: false, error: error.error?.message || `HTTP ${response.status}` };
    }

    const data = await response.json();
    const models: ModelInfo[] = (data.data || [])
      .filter((m: any) => {
        const p = m.pricing;
        if (!p) return false;
        return p.prompt === '0' && p.completion === '0';
      })
      .map((m: any) => ({
        id: m.id,
        name: m.name || m.id.replace(/\//g, ' - '),
        context: m.context_length
          ? m.context_length > 1000000
            ? `${Math.round(m.context_length / 1000000)}M`
            : `${Math.round(m.context_length / 1000)}K`
          : 'Unknown',
      }));

    if (models.length === 0) {
      return { models: [], valid: false, error: 'No free models available' };
    }

    return { models, valid: true };
  } catch (error) {
    return { models: [], valid: false, error: (error as Error).message };
  }
}

export async function fetchOpenRouterModelsViaWorker(): Promise<{ models: ModelInfo[]; valid: boolean; error?: string }> {
  try {
    const response = await fetch(`${WORKER_ENDPOINT.replace('/api/generate', '')}/api/models?provider=openrouter`);
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      return { models: [], valid: false, error: error.error || `HTTP ${response.status}` };
    }
    const data = await response.json();
    const models: ModelInfo[] = (data.data || [])
      .filter((m: any) => {
        const p = m.pricing;
        if (!p) return false;
        return p.prompt === '0' && p.completion === '0';
      })
      .map((m: any) => ({
        id: m.id,
        name: m.name || m.id.replace(/\//g, ' - '),
        context: m.context_length
          ? m.context_length > 1000000
            ? `${Math.round(m.context_length / 1000000)}M`
            : `${Math.round(m.context_length / 1000)}K`
          : 'Unknown',
      }));
    return models.length > 0 ? { models, valid: true } : { models: [], valid: false, error: 'No free models available' };
  } catch (error) {
    return { models: [], valid: false, error: (error as Error).message };
  }
}

export function getDefaultModels(provider: string): ModelInfo[] {
  switch (provider) {
    case 'groq':
      return [
        { id: 'llama-3.1-8b-instant', name: 'Llama 3.1 8B (Fast)', context: '8K' },
        { id: 'llama-3.2-1b-preview', name: 'Llama 3.2 1B (Fastest)', context: '8K' },
        { id: 'llama-3.2-3b-preview', name: 'Llama 3.2 3B', context: '8K' },
        { id: 'mixtral-8x7b-32768', name: 'Mixtral 8x7B', context: '32K' },
        { id: 'llama-3.1-70b-versatile', name: 'Llama 3.1 70B (Large)', context: '128K' },
      ];
    case 'gemini':
      return [
        { id: 'gemini-2.0-flash-exp', name: 'Gemini 2.0 Flash', context: '1M' },
        { id: 'gemini-1.5-flash', name: 'Gemini 1.5 Flash', context: '128K' },
        { id: 'gemini-1.5-pro', name: 'Gemini 1.5 Pro', context: '2M' },
      ];
    case 'openrouter':
      return [
        { id: 'google/gemma-4-31b-it:free', name: 'Gemma 4 31B (Free)', context: '262K' },
        { id: 'nvidia/nemotron-3-ultra-550b-a55b:free', name: 'Nemotron 3 Ultra 550B (Free)', context: '1000K' },
        { id: 'nvidia/nemotron-3-super-120b-a12b:free', name: 'Nemotron 3 Super 120B (Free)', context: '262K' },
        { id: 'openai/gpt-oss-20b:free', name: 'GPT-OSS 20B (Free)', context: '131K' },
        { id: 'poolside/laguna-s-2.1:free', name: 'Laguna S 2.1 (Free)', context: '262K' },
      ];
    case 'pollination':
    default:
      return [
        { id: 'openai-fast', name: 'OpenAI Fast (Recommended)', context: '8K' },
        { id: 'openai', name: 'OpenAI (Default)', context: '8K' },
        { id: 'gpt-oss', name: 'GPT OSS', context: '8K' },
      ];
  }
}

export function createDefaultAIProvider(): AIProvider {
  return {
    id: 'pollination',
    name: 'Pollination AI',
    endpoint: POLLINATION_ENDPOINT,
    apiKey: '',
    enabled: true,
    models: ['openai-fast', 'openai', 'gpt-oss'],
    selectedModel: 'openai-fast',
    maxTokens: 2000,
    temperature: 0.7,
  };
}

export { POLLINATION_ENDPOINT };